export interface Permissions {
  // View permissions
  viewDashboard: boolean;
  viewLeaderboard: boolean;
  viewUpcomingFights: boolean;
  viewBoxerProfiles: boolean;
  viewBoxerDirectory: boolean;
  viewFightCards: boolean;
  viewSponsors: boolean;
  viewLicenses: boolean;
  viewBetting: boolean;
  viewTournaments: boolean;
  viewRankings: boolean;
  viewSettings: boolean;
  viewHelp: boolean;
  
  // Action permissions
  registerBoxers: boolean;
  editBoxers: boolean;
  deleteBoxers: boolean;
  generateFightCards: boolean;
  editFightCards: boolean;
  declareFightResults: boolean;
  registerSponsors: boolean;
  editSponsors: boolean;
  deleteSponsors: boolean;
  manageLicenses: boolean;
  manageBetting: boolean;
  settleBets: boolean;
  manageTournaments: boolean;
  editRankingSettings: boolean;
  editAppSettings: boolean;
  resetSeason: boolean;
  importExportData: boolean;
  manageUsers: boolean;
  
  // Employee & Document permissions
  manageEmployees: boolean;
  viewSignUpStats: boolean;
  manageDocuments: boolean;
  viewDocuments: boolean;
}

export const DEFAULT_ADMIN_PERMISSIONS: Permissions = {
  viewDashboard: true,
  viewLeaderboard: true,
  viewUpcomingFights: true,
  viewBoxerProfiles: true,
  viewBoxerDirectory: true,
  viewFightCards: true,
  viewSponsors: true,
  viewLicenses: true,
  viewBetting: true,
  viewTournaments: true,
  viewRankings: true,
  viewSettings: true,
  viewHelp: true,
  registerBoxers: true,
  editBoxers: true,
  deleteBoxers: true,
  generateFightCards: true,
  editFightCards: true,
  declareFightResults: true,
  registerSponsors: true,
  editSponsors: true,
  deleteSponsors: true,
  manageLicenses: true,
  manageBetting: true,
  settleBets: true,
  manageTournaments: true,
  editRankingSettings: true,
  editAppSettings: true,
  resetSeason: true,
  importExportData: true,
  manageUsers: true,
  manageEmployees: true,
  viewSignUpStats: true,
  manageDocuments: true,
  viewDocuments: true,
};

export const DEFAULT_STAFF_PERMISSIONS: Permissions = {
  viewDashboard: true,
  viewLeaderboard: true,
  viewUpcomingFights: true,
  viewBoxerProfiles: true,
  viewBoxerDirectory: true,
  viewFightCards: true,
  viewSponsors: true,
  viewLicenses: true,
  viewBetting: true,
  viewTournaments: false,
  viewRankings: true,
  viewSettings: false,
  viewHelp: true,
  registerBoxers: false,
  editBoxers: false,
  deleteBoxers: false,
  generateFightCards: false,
  editFightCards: false,
  declareFightResults: false,
  registerSponsors: false,
  editSponsors: false,
  deleteSponsors: false,
  manageLicenses: false,
  manageBetting: false,
  settleBets: false,
  manageTournaments: false,
  editRankingSettings: false,
  editAppSettings: false,
  resetSeason: false,
  importExportData: false,
  manageUsers: false,
  manageEmployees: false,
  viewSignUpStats: false,
  manageDocuments: false,
  viewDocuments: false,
};

export const DEFAULT_BOXER_PERMISSIONS: Permissions = {
  viewDashboard: false,
  viewLeaderboard: true,
  viewUpcomingFights: true,
  viewBoxerProfiles: true,
  viewBoxerDirectory: true,
  viewFightCards: true,
  viewSponsors: false,
  viewLicenses: false,
  viewBetting: false,
  viewTournaments: false,
  viewRankings: true,
  viewSettings: false,
  viewHelp: true,
  registerBoxers: false,
  editBoxers: false,
  deleteBoxers: false,
  generateFightCards: false,
  editFightCards: false,
  declareFightResults: false,
  registerSponsors: false,
  editSponsors: false,
  deleteSponsors: false,
  manageLicenses: false,
  manageBetting: false,
  settleBets: false,
  manageTournaments: false,
  editRankingSettings: false,
  editAppSettings: false,
  resetSeason: false,
  importExportData: false,
  manageUsers: false,
  manageEmployees: false,
  viewSignUpStats: false,
  manageDocuments: false,
  viewDocuments: false,
};

export const DEFAULT_SPONSOR_PERMISSIONS: Permissions = {
  viewDashboard: false,
  viewLeaderboard: true,
  viewUpcomingFights: true,
  viewBoxerProfiles: true,
  viewBoxerDirectory: true,
  viewFightCards: true,
  viewSponsors: false,
  viewLicenses: false,
  viewBetting: false,
  viewTournaments: false,
  viewRankings: true,
  viewSettings: false,
  viewHelp: true,
  registerBoxers: false,
  editBoxers: false,
  deleteBoxers: false,
  generateFightCards: false,
  editFightCards: false,
  declareFightResults: false,
  registerSponsors: false,
  editSponsors: false,
  deleteSponsors: false,
  manageLicenses: false,
  manageBetting: false,
  settleBets: false,
  manageTournaments: false,
  editRankingSettings: false,
  editAppSettings: false,
  resetSeason: false,
  importExportData: false,
  manageUsers: false,
  manageEmployees: false,
  viewSignUpStats: false,
  manageDocuments: false,
  viewDocuments: false,
};

export const DEFAULT_LEGAL_TEAM_PERMISSIONS: Permissions = {
  viewDashboard: false,
  viewLeaderboard: true,
  viewUpcomingFights: true,
  viewBoxerProfiles: true,
  viewBoxerDirectory: true,
  viewFightCards: true,
  viewSponsors: true,
  viewLicenses: true,
  viewBetting: false,
  viewTournaments: true,
  viewRankings: true,
  viewSettings: false,
  viewHelp: true,
  registerBoxers: false,
  editBoxers: false,
  deleteBoxers: false,
  generateFightCards: false,
  editFightCards: false,
  declareFightResults: false,
  registerSponsors: false,
  editSponsors: false,
  deleteSponsors: false,
  manageLicenses: false,
  manageBetting: false,
  settleBets: false,
  manageTournaments: false,
  editRankingSettings: false,
  editAppSettings: false,
  resetSeason: false,
  importExportData: false,
  manageUsers: false,
  manageEmployees: false,
  viewSignUpStats: false,
  manageDocuments: true,
  viewDocuments: true,
};

export interface User {
  id: string;
  username: string;
  password: string; // In real production, this should be hashed
  role: 'admin' | 'staff' | 'boxer' | 'sponsor' | 'legal-team';
  permissions?: Permissions;
  linkedEntityId?: string; // Links to boxer or sponsor ID
  createdAt: string;
  lastLogin?: string;
}

export interface AuthSession {
  user: Omit<User, 'password'>;
  loginTime: string;
}

export interface RegistrationRequest {
  id: string;
  stateId: string;
  username: string;
  passwordHash: string;
  name: string;
  role: 'staff' | 'boxer' | 'sponsor' | '1068-staff' | 'legal-team';
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
}

export interface Employee {
  id: string;
  stateId: string;
  name: string;
  phoneNumber: string;
  isBoxer: boolean;
  isSponsor: boolean;
  position: string;
  hiredDate: string;
  salary?: number;
  notes?: string;
  createdAt: string;
  createdBy: string;
}

export interface Document {
  id: string;
  title: string;
  category: 'rules' | 'agreement' | 'policy' | 'form' | 'other';
  content: string;
  version: string;
  uploadedBy: string;
  uploadedAt: string;
  lastModifiedBy?: string;
  lastModifiedAt?: string;
  isActive: boolean;
}
